import {
  EMAIL_PREVIEW_LINK_OUTLINE_OFFSET_PX,
  EMAIL_PREVIEW_LINK_OUTLINE_WIDTH_PX,
} from "./emailPreviewLinkHighlight";

export interface IEmailTopLinkCalloutGeometry {
  badge: { top: number; left: number };
  line: { x1: number; y1: number; x2: number; y2: number };
}

export interface IViewportRect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

const CALLOUT_EDGE_INSET_PX = 8;
const CALLOUT_LINE_LENGTH_PX = 16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Translate an iframe element's viewport rect into the parent page's viewport.
 * `getBoundingClientRect()` on iframe content is relative to the iframe, not the page.
 */
export function getIframeElementPageRect(
  iframe: HTMLIFrameElement,
  element: HTMLElement,
): IViewportRect | null {
  const elementRect = element.getBoundingClientRect();
  if (elementRect.width <= 0 || elementRect.height <= 0) {
    return null;
  }

  const frame = element.ownerDocument.defaultView?.frameElement;
  const frameElement = frame instanceof HTMLElement ? frame : iframe;
  const frameRect = frameElement.getBoundingClientRect();
  const style = frameElement.ownerDocument.defaultView?.getComputedStyle(frameElement);
  const offsetTop =
    (style != null ? parseFloat(style.borderTopWidth) || 0 : 0) +
    (style != null ? parseFloat(style.paddingTop) || 0 : 0);
  const offsetLeft =
    (style != null ? parseFloat(style.borderLeftWidth) || 0 : 0) +
    (style != null ? parseFloat(style.paddingLeft) || 0 : 0);

  const top = frameRect.top + offsetTop + elementRect.top;
  const left = frameRect.left + offsetLeft + elementRect.left;

  return {
    top,
    left,
    width: elementRect.width,
    height: elementRect.height,
    right: left + elementRect.width,
    bottom: top + elementRect.height,
  };
}

export function measureEmailPreviewCalloutGeometry(
  overlayLayer: HTMLElement,
  targetPageRect: IViewportRect,
  badgeWidth: number,
  badgeHeight: number,
): IEmailTopLinkCalloutGeometry | null {
  const overlayRect = overlayLayer.getBoundingClientRect();
  if (overlayRect.width <= 0 || overlayRect.height <= 0 || badgeWidth <= 0 || badgeHeight <= 0) {
    return null;
  }

  const outlineExtent = EMAIL_PREVIEW_LINK_OUTLINE_OFFSET_PX + EMAIL_PREVIEW_LINK_OUTLINE_WIDTH_PX;
  const highlight = {
    top: targetPageRect.top - overlayRect.top - outlineExtent,
    left: targetPageRect.left - overlayRect.left - outlineExtent,
    width: targetPageRect.width + outlineExtent * 2,
    height: targetPageRect.height + outlineExtent * 2,
  };

  const badgeTop = clamp(
    highlight.top + highlight.height / 2 - badgeHeight / 2,
    CALLOUT_EDGE_INSET_PX,
    Math.max(CALLOUT_EDGE_INSET_PX, overlayLayer.clientHeight - badgeHeight - CALLOUT_EDGE_INSET_PX),
  );

  const rightBadgeLeft = highlight.left + highlight.width + CALLOUT_LINE_LENGTH_PX;
  const leftBadgeLeft = highlight.left - CALLOUT_LINE_LENGTH_PX - badgeWidth;
  const maxBadgeLeft = overlayLayer.clientWidth - badgeWidth - CALLOUT_EDGE_INSET_PX;
  const canPlaceRight = rightBadgeLeft <= maxBadgeLeft;
  const canPlaceLeft = leftBadgeLeft >= CALLOUT_EDGE_INSET_PX;

  const badgeLeft = canPlaceRight
    ? rightBadgeLeft
    : canPlaceLeft
      ? leftBadgeLeft
      : clamp(rightBadgeLeft, CALLOUT_EDGE_INSET_PX, Math.max(CALLOUT_EDGE_INSET_PX, maxBadgeLeft));

  const lineY = highlight.top + highlight.height / 2;
  const placeRight = badgeLeft >= highlight.left + highlight.width / 2;
  const line = placeRight
    ? {
        x1: highlight.left + highlight.width,
        y1: lineY,
        x2: badgeLeft,
        y2: badgeTop + badgeHeight / 2,
      }
    : {
        x1: highlight.left,
        y1: lineY,
        x2: badgeLeft + badgeWidth,
        y2: badgeTop + badgeHeight / 2,
      };

  return {
    badge: { top: badgeTop, left: badgeLeft },
    line,
  };
}
